import { useCallback, useMemo, useState, type ReactNode } from 'react';

import {
  DndContext,
  DragOverlay,
  pointerWithin,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { useTranslation } from '@mint-lib/i18n';
import { Card } from '@mint-lib/tw-components';

import type { MoveVerdict } from '../../../../helpers/fileSystemMove.js';
import { getNodeName } from '../../../../helpers/fileSystemNode.js';
import type { FileSystemNode } from '../../../../types/fileSystem.js';
import { FileIcon } from '../FileIcon/FileIcon.jsx';
import { FileSystemDndContext } from './dndContext.js';
import { fromDropId } from './dndIds.js';

/**
 * Pointer position first, overlap area as the fallback. A dragged card is much
 * wider than a breadcrumb, so pure rect intersection would hand the drop to
 * whatever it overlaps most and small targets become unhittable.
 */
const collisionDetection: CollisionDetection = (args) => {
  const byPointer = pointerWithin(args);

  return byPointer.length ? byPointer : rectIntersection(args);
};

type Props = {
  children: ReactNode;
  selectedNodes: FileSystemNode[];
  checkMove: (nodes: FileSystemNode[], targetFolderId: string) => MoveVerdict;
  onMoveNodes: (nodes: FileSystemNode[], targetFolderId: string) => void;
};

const FileSystemDndProvider = ({
  children,
  selectedNodes,
  checkMove,
  onMoveNodes,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const [activeNodes, setActiveNodes] = useState<FileSystemNode[]>([]);
  const [overFolderId, setOverFolderId] = useState<string | null>(null);

  const sensors = useSensors(
    // A small threshold keeps a plain click on a tile (open, rename, select)
    // from being swallowed by the drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor),
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const node = event.active.data.current?.node as
        | FileSystemNode
        | undefined;

      if (!node) return;

      // Dragging a node inside the current selection moves the whole selection,
      // dragging anything else moves just that node — the Drive behaviour.
      const isSelected = selectedNodes.some(
        (selected) => selected.kind === node.kind && selected.id === node.id,
      );

      setActiveNodes(
        isSelected && selectedNodes.length ? selectedNodes : [node],
      );
    },
    [selectedNodes],
  );

  const reset = useCallback(() => {
    setActiveNodes([]);
    setOverFolderId(null);
  }, []);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => setOverFolderId(fromDropId(event.over?.id)),
    [],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const targetFolderId = fromDropId(event.over?.id);
      const nodes = activeNodes;

      reset();

      if (!targetFolderId || !nodes.length) return;
      if (!checkMove(nodes, targetFolderId).allowed) return;

      onMoveNodes(nodes, targetFolderId);
    },
    [activeNodes, checkMove, onMoveNodes, reset],
  );

  const isOverAllowed = useMemo(
    () =>
      Boolean(
        overFolderId &&
        activeNodes.length &&
        checkMove(activeNodes, overFolderId).allowed,
      ),
    [overFolderId, activeNodes, checkMove],
  );

  const value = useMemo(
    () => ({
      activeNodes,
      overFolderId,
      isOverAllowed,
      checkMove,
      onMoveNodes,
    }),
    [activeNodes, overFolderId, isOverAllowed, checkMove, onMoveNodes],
  );

  const [previewNode] = activeNodes;

  return (
    <FileSystemDndContext.Provider value={value}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={reset}
      >
        {children}

        <DragOverlay dropAnimation={null}>
          {previewNode ? (
            <Card
              variant="outlined"
              className="w-fit cursor-grabbing shadow-lg"
              contentClassName="flex flex-row items-center gap-2 p-2"
            >
              <FileIcon type={previewNode.fileType} />
              <span className="max-w-40 truncate">
                {activeNodes.length > 1
                  ? t('{{total}} items', { total: activeNodes.length })
                  : getNodeName(previewNode)}
              </span>
            </Card>
          ) : null}
        </DragOverlay>
      </DndContext>
    </FileSystemDndContext.Provider>
  );
};

export { FileSystemDndProvider };
